import React, { useState, memo } from "react";
import {
    ArrowLeft, Save, FileText, RefreshCw, ChevronDown, ChevronUp, Trash2, Printer, X, Building2, FileCheck, ClipboardList,
    ClipboardCheck, Route, Anchor, Package, Truck, Warehouse, DollarSign, Paperclip, Edit2, Info, User, PenLine, Ship, MapPin,
    Container, Calendar, Flag, Hash, Truck as TruckIcon, Anchor as AnchorIcon, Navigation, Globe, Clock, CheckCircle, XCircle
} from 'lucide-react';
import DateInputwithcal from "../ui/DateInputwithcal";
import { SearchBarPagination } from "../SearchBox/SearchBarPagination";
import { InlineCountryFlag } from "../shipment/CountryBadge";

function RoutingInformation({
    formData: externalFormData,
    onChange,
    inputClass: propInputClass,
    selectClass: propSelectClass,
    labelClass: propLabelClass,
    handleDateChange,
    handleDateError,
    requiredFields = [],  // 👈 NEW: array of field names that are required
    countryList = [], // 👈 NEW: list of countries for dropdowns
    moveTypeList = [],
    errors = {},  // 👈 NEW: list of move types for dropdow    n
    setAddress,
    coloaderList = [],
}) {
    /** Keys from ImportBookingForm `requiredFields` (aliases) mapped to PascalCase payload fields */
    const REQUIRED_FIELD_ALIASES = {
        CarrierName: "carrier",
        VesselName: "motherVessel",
        PortOfLoading: "portOfLoading",
        PortOfDischarge: "portOfDischarge",
        EtdAtPol: "etdAtPol",
    };

    const [localFormData, setLocalFormData] = useState({
        CoLoader: "",
        CarrierName: "",
        CarrierID: 0,
        CarrierSCAC: "",
        VesselId: 0,
        VesselName: "",
        Voyage: "",
        ConMotherVessel: "",
        ConMotherVoyage: "",
        RI_Flag: "",
        RI_Lloyds: "",
        RI_Year: "",
        PreCarrageBy: "",
        PalceOfReceipt: "",
        PalceOfReceiptCode: "",
        PalceOfReceipt_Customized: "",
        PortOfLoading: "",
        PortOfLoadingCode: "",
        PortOfLoading_Customized: "",
        PortofTransshipment: "",
        PortofTransshipment_LOCode: "",
        PortofTransshipment_Customized: "",
        TranshipmentVessel: "",
        TranshipmentVoyage: "",
        PortOfDischarge: "",
        PortOfDischargeCode: "",
        PortOfDischarge_Customized: "",
        LoadingPierTerminal: "",
        ClearancePort: "",
        ClearancePortCode: "",
        CarrierPlaceOfDelivery: "",
        CarrierPlaceOfDeliveryCode: "",
        CarrierPlaceOfDelivery_Customized: "",
        ShipezPlaceOfDelivery: "",
        ShipezPlaceOfDeliveryCode: "",
        ShipezPlaceOfDeliveryCustomized: "",
        FreightLocationName: "",
        FreightLocationID: 0,
        FreightLocationAddress: "",
        FirmsCode: "",
        EntryNo: "",
        CountryOfOrigin: "",
        MoveType: "",
        MoveTypeHideOnHBL: false,
        IsForAMS: false,
        CommercialDocumentsReceivedDate: null,
        ContainersAvailableDate: null,
        AMS_CODate: null,
        AMSFilingDate: null,
        ISFDetailsReceivedDate: null,
        ISF_CODate: null,
        ISFFilingDate: null,
        Cargo_CODate: null,
        EtdAtPol: null,
        ContainerOnboardDate: null,
        ETDAtTransshipment: null,
        ETAPOD: null,
        ArrivalNoticeETA: null,
        PlaceofDeliveryETA: null,
        ETADestination: null,
        CarrierMBLReceivedDate: null,
        DeliveryStatus: "",
        LiveUnloadDate: null,
        DropDate: null,
        ContnrPickupDate: null,
        ITNumber: "",
        ITPlace: "",
        ITDate: null,
        LastFreeDate: null,
        CarrierReleaseDate: null,
        CustomsBrokerageRequiredDate: null,
        CustomsReleaseDate: null,
        CustomsholdExam: "",
        DeliveryOrderSentDate: null,
        CarrierArrivalNoticeReceivedDate: null,
        ArrivalNoticeSenttoBrokerDate: null,
        FreightChargesPaidDate: null,
        InvoiceDate: null,
        InvoiceNumber: "",
    });

    const formData = externalFormData || localFormData;

    const getFieldValue = (upper, lower) => {
        return formData?.[upper] ?? formData?.[lower];
    };

    const PortOfLoading = getFieldValue('PortOfLoading', 'portOfLoading');
    const PortOfLoadingCode = getFieldValue('PortOfLoadingCode', 'portOfLoadingCode');
    const PortOfDischarge = getFieldValue('PortOfDischarge', 'portOfDischarge');
    const PortOfDischargeCode = getFieldValue('PortOfDischargeCode', 'portOfDischargeCode');

    /** Normalized delivery status: API uses "" for “no selection”; legacy local default was "noStatus". */
    const deliveryStatusNormalized = formData?.DeliveryStatus === 'noStatus' ? '' : (formData?.DeliveryStatus ?? '');

    const handleChange = (e) => {
        if (typeof onChange === "function") {
            return onChange(e);
        }
        const { name, value, type, checked } = e.target;
        const nextVal = type === "checkbox" ? checked : value;
        setLocalFormData((prev) => ({
            ...prev,
            [name]: nextVal,
        }));
    };

    /** When clearing a port-type search, reset name + UN/LOC code to match ImportBooking payload */
    const clearLocationPair = (nameKey, codeKey) => {
        handleChange({ target: { name: nameKey, value: '' } });
        handleChange({ target: { name: codeKey, value: '' } });
    };

    const isRequired = (fieldName) =>
        requiredFields.includes(fieldName) ||
        (REQUIRED_FIELD_ALIASES[fieldName] != null &&
            requiredFields.includes(REQUIRED_FIELD_ALIASES[fieldName]));

    const inputClass = propInputClass ||
        "w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-slate-800 dark:text-white transition-all";
    const selectClass = propSelectClass || inputClass;
    const labelClass = propLabelClass || "block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1";

    const dateFields = [
        { label: "AMS Cutoff Date", key: "AMS_CODate" },
        { label: "AMS Filing Date", key: "AMSFilingDate" },
        { label: "ISF Details Received", key: "ISFDetailsReceivedDate" },
        { label: "ISF Cutoff Date", key: "ISF_CODate" },
        { label: "ISF Filing Date", key: "ISFFilingDate" },
        { label: "Cargo Cutoff Date", key: "Cargo_CODate" },
        { label: "ETD at POL", key: "EtdAtPol" },
        { label: "Container Onboard", key: "ContainerOnboardDate" },
        { label: "ETD at Transshipment", key: "ETDAtTransshipment" },
        { label: "ETA at POD", key: "ETAPOD" },
        { label: "Arrival Notice ETA", key: "ArrivalNoticeETA" },
        { label: "Place of Delivery ETA", key: "PlaceofDeliveryETA" },
        { label: "ETA at Destination", key: "ETADestination" },
        { label: "Carrier MBL Received Date", key: "CarrierMBLReceivedDate" },
    ];

    const deliveryFields = [
        { label: "Live Unload Date", key: "LiveUnloadDate", type: "date" },
        { label: "Drop Date", key: "DropDate", type: "date" },
        { label: "Pickup Date", key: "ContnrPickupDate", type: "date" },
        { label: "IT Number", key: "ITNumber", type: "text" },
        { label: "IT Port", key: "ITPlace", type: "text" },
        { label: "IT Date", key: "ITDate", type: "date" },
        { label: "Last Free Date", key: "LastFreeDate", type: "date" },
        { label: "Carrier Release Date", key: "CarrierReleaseDate", type: "date" },
        { label: "Customs Brokerage Required Date #", key: "CustomsBrokerageRequiredDate", type: "date" },
        { label: "Customs Release Date", key: "CustomsReleaseDate", type: "date" },
        { label: "Customs Hold/Exam", key: "CustomsholdExam", type: "date" },
        { label: "Delivery Order Sent Date", key: "DeliveryOrderSentDate", type: "date" },
    ];

    const deliveryFields1 = [
        { label: "Carrier Arrival Notice Received Date", key: "CarrierArrivalNoticeReceivedDate", type: "date" },
        { label: "Arrival Notice Sent Date", key: "ArrivalNoticeSenttoBrokerDate", type: "date" },
        { label: "Freight Charges Paid Date", key: "FreightChargesPaidDate", type: "date" },
        { label: "Invoice Date", key: "InvoiceDate", type: "date" },
        { label: "Customer Invoice Number", key: "InvoiceNumber", type: "text" },
    ];

    const _getPortCode = (strdata) => {
        if (!strdata) return "";
        const match = strdata.match(/\[(.*?)\]/);
        return match ? match[1] : "";
    }

    return (
        <div className="space-y-6">
            {/* Visual Route Display */}
            <div className="mb-6 px-6 py-4 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-11 h-11 flex-shrink-0 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-md shadow-blue-400/30">
                            <MapPin className="w-5 h-5 text-white" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[10px] font-semibold tracking-widest text-slate-400 dark:text-slate-500 uppercase">
                                Origin
                            </p>
                            <p className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
                                <InlineCountryFlag code={PortOfLoadingCode} />
                                <span>{PortOfLoading || "Port of Loading"}</span>
                            </p>
                        </div>
                    </div>
                    <div className="flex-1 flex items-center relative mx-2 h-8">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                        <div className="flex-1 h-0.5 self-center bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 relative">
                            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                                <div className="w-9 h-9 rounded-full bg-white dark:bg-slate-900 border-2 border-slate-200 flex items-center justify-center shadow-md shadow-cyan-300/20">
                                    <Ship className="w-4 h-4 text-cyan-500" />
                                </div>
                            </div>
                        </div>
                        <div className="w-1.5 h-1.5 rounded-full bg-teal-400 flex-shrink-0" />
                    </div>
                    <div className="flex items-center gap-3 min-w-0 flex-row-reverse">
                        <div className="w-11 h-11 flex-shrink-0 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center shadow-md shadow-teal-400/30">
                            <MapPin className="w-5 h-5 text-white" />
                        </div>
                        <div className="min-w-0 text-right">
                            <p className="text-[10px] font-semibold tracking-widest text-slate-400 dark:text-slate-500 uppercase">
                                Destination
                            </p>
                            <p className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
                                <InlineCountryFlag code={PortOfDischargeCode} />
                                <span>
                                    {PortOfDischarge || "Port of Discharge"}
                                </span>
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* LEFT PANEL */}
                <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className={labelClass}>
                                Co-loader {!!isRequired("CoLoader") && <span className="text-red-500">*</span>}
                            </label>
                            <select className={selectClass} name="CoLoader" value={formData?.CoLoader ?? ''} onChange={handleChange}>
                                <option value="">Select</option>
                                {coloaderList?.map((coloader) => (
                                    <option key={coloader.id} value={coloader.addressName}>{coloader.addressName}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className={labelClass}>
                                Carrier {!!isRequired("carrier") && <span className="text-red-500">*</span>}
                            </label>
                            <SearchBarPagination
                                searchAPI="v1/AutoSuggestion/GetCarrierSuggestion?Prefix="
                                selectReacord={(d) => {
                                    handleChange({ target: { name: 'CarrierName', value: d?.[1] } });
                                    handleChange({ target: { name: 'CarrierID', value: d?.[0] } });
                                    handleChange({ target: { name: 'CarrierSCAC', value: String(d?.[2] ?? '') } });
                                }}
                                value={formData?.CarrierName || ''}
                                placeholder="Select carrier"
                                className="bg-white dark:bg-slate-900 border border-slate-700 rounded-lg text-sm text-white"
                            />
                            {errors.CarrierName && (
                                <p className="text-red-500 text-xs mt-1">{errors.CarrierName}</p>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className={labelClass}>
                                Vessel {!!isRequired("VesselName") && <span className="text-red-500">*</span>}
                            </label>
                            <SearchBarPagination
                                searchAPI="v1/AutoSuggestion/GetVesselList?Prefix="
                                selectReacord={(d) => {
                                    handleChange({ target: { name: 'VesselName', value: d?.[1] } });
                                    handleChange({ target: { name: 'VesselId', value: String(d?.[0] ?? '') } });
                                }}
                                value={formData?.VesselName || ''}
                                placeholder="Select vessel"
                                className="bg-white dark:bg-slate-900 border border-slate-700 rounded-lg text-sm text-white"
                                onchange={(val) => { if (!val) { handleChange({ target: { name: 'VesselName', value: '' } }); handleChange({ target: { name: 'VesselId', value: '0' } }); } }}
                            />
                        </div>
                        <div>
                            <label className={labelClass}>
                                Voyage {!!isRequired("Voyage") && <span className="text-red-500">*</span>}
                            </label>
                            <input className={inputClass} name="Voyage" value={formData?.Voyage ?? ''} onChange={handleChange} />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className={labelClass}>
                                Connecting Mother Vessel {!!isRequired("ConMotherVessel") && <span className="text-red-500">*</span>}
                            </label>
                            <SearchBarPagination
                                searchAPI="v1/AutoSuggestion/GetVesselList?Prefix="
                                selectReacord={(d) => {
                                    handleChange({ target: { name: 'ConMotherVessel', value: d?.[1] ?? '' } });
                                }}
                                value={formData?.ConMotherVessel ?? ''}
                                placeholder="Select connecting vessel"
                                className="bg-white dark:bg-slate-900 border border-slate-700 rounded-lg text-sm text-white"
                                onchange={(val) => { if (!val) handleChange({ target: { name: 'ConMotherVessel', value: '' } }); }}
                            />
                        </div>
                        <div>
                            <label className={labelClass}>
                                Connecting Mother Voyage {!!isRequired("ConMotherVoyage") && <span className="text-red-500">*</span>}
                            </label>
                            <input className={inputClass} name="ConMotherVoyage" value={formData?.ConMotherVoyage ?? ''} onChange={handleChange} />
                        </div>
                    </div>

                    {/* Vessel Particulars Card */}
                    <div className="bg-gradient-to-br from-slate-50 to-cyan-50 dark:from-slate-900/50 dark:to-cyan-950/20 rounded-xl border-l-2 border-l-cyan-500 border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="p-1.5 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg">
                                <Flag className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                            </div>
                            <h3 className="font-semibold text-cyan-700 dark:text-cyan-400 text-sm">Vessel Particulars</h3>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <label className={labelClass}>Flag {isRequired("flag") && <span className="text-red-500">*</span>}</label>
                                <input className={inputClass} name="RI_Flag" value={formData?.RI_Flag ?? ''} onChange={handleChange} />
                            </div>
                            <div>
                                <label className={labelClass}>Lloyds # {isRequired("lloydsNumber") && <span className="text-red-500">*</span>}</label>
                                <input className={inputClass} name="RI_Lloyds" value={formData?.RI_Lloyds ?? ''} onChange={handleChange} />
                            </div>
                            <div>
                                <label className={labelClass}>Year {isRequired("year") && <span className="text-red-500">*</span>}</label>
                                <input className={inputClass} name="RI_Year" value={formData?.RI_Year ?? ''} onChange={handleChange} />
                            </div>
                        </div>
                        <div className="mt-2">
                            <label className={labelClass}>Pre-Carriage By {isRequired("PreCarrageBy") && <span className="text-red-500">*</span>}</label>
                            <input className={inputClass} name="PreCarrageBy" value={formData?.PreCarrageBy ?? ''} onChange={handleChange} />
                        </div>
                    </div>

                    {/* Ports & Locations Card */}
                    <div className="bg-gradient-to-br from-slate-50 to-indigo-50 dark:from-slate-900/50 dark:to-indigo-950/20 rounded-xl border-l-2 border-l-indigo-500 border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="p-1.5 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                                <Navigation className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                            </div>
                            <h3 className="font-semibold text-indigo-700 dark:text-indigo-400 text-sm">Ports & Locations</h3>
                        </div>
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelClass}>Place of Receipt {isRequired("PalceOfReceipt") && <span className="text-red-500">*</span>}</label>
                                    <SearchBarPagination
                                        searchAPI={'v1/AutoSuggestion/GetDestinationSuggestion?Prefix='}
                                        selectReacord={(d) => {
                                            handleChange({ target: { name: 'PalceOfReceipt', value: d?.[1] } });
                                            handleChange({ target: { name: 'PalceOfReceiptCode', value: _getPortCode(d?.[1]) } });
                                            handleChange({ target: { name: 'PalceOfReceipt_Customized', value: d?.[1] ?? '' } });
                                        }}
                                        value={formData?.PalceOfReceipt ?? ''}
                                        onchange={(val) => { if (!val) clearLocationPair('PalceOfReceipt', 'PalceOfReceiptCode'); }}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Show On HBL {isRequired("PalceOfReceipt_Customized") && <span className="text-red-500">*</span>}</label>
                                    <input className={inputClass} placeholder="Show On HBL" name="PalceOfReceipt_Customized" value={formData?.PalceOfReceipt_Customized ?? ''} onChange={handleChange} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelClass}>Port of Loading {isRequired("PortOfLoading") && <span className="text-red-500">*</span>}</label>
                                    <SearchBarPagination
                                        searchAPI={'v1/AutoSuggestion/GetDestinationSuggestion?Prefix='}
                                        selectReacord={(d) => {
                                            handleChange({ target: { name: 'PortOfLoading', value: d?.[1] } });
                                            handleChange({ target: { name: 'PortOfLoadingCode', value: _getPortCode(d?.[1]) } });
                                            handleChange({ target: { name: 'PortOfLoading_Customized', value: d?.[1] ?? '' } });
                                        }}
                                        value={PortOfLoading}
                                        onchange={(val) => { if (!val) clearLocationPair('PortOfLoading', 'PortOfLoadingCode'); }}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Show On HBL {isRequired("PortOfLoading_Customized") && <span className="text-red-500">*</span>}</label>
                                    <input className={inputClass} placeholder="Show On HBL" name="PortOfLoading_Customized" value={formData?.PortOfLoading_Customized ?? ''} onChange={handleChange} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelClass}>Transshipment {isRequired("PortofTransshipment") && <span className="text-red-500">*</span>}</label>
                                    <SearchBarPagination
                                        searchAPI={'v1/AutoSuggestion/GetDestinationSuggestion?Prefix='}
                                        selectReacord={(d) => {
                                            handleChange({ target: { name: 'PortofTransshipment', value: d?.[1] } });
                                            handleChange({ target: { name: 'PortofTransshipment_LOCode', value: _getPortCode(d?.[1]) } });
                                            handleChange({ target: { name: 'PortofTransshipment_Customized', value: d?.[1] ?? '' } });
                                        }}
                                        value={formData?.PortofTransshipment ?? ''}
                                        onchange={(val) => { if (!val) clearLocationPair('PortofTransshipment', 'PortofTransshipment_LOCode'); }}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Show On HBL {isRequired("PortofTransshipment_Customized") && <span className="text-red-500">*</span>}</label>
                                    <input className={inputClass} placeholder="Show On HBL" name="PortofTransshipment_Customized" value={formData?.PortofTransshipment_Customized ?? ''} onChange={handleChange} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelClass}>Transshipment Vessel {isRequired("TranshipmentVessel") && <span className="text-red-500">*</span>}</label>
                                    <SearchBarPagination
                                        searchAPI="v1/AutoSuggestion/GetVesselList?Prefix="
                                        selectReacord={(d) => handleChange({ target: { name: 'TranshipmentVessel', value: d?.[1] ?? '' } })}
                                        value={formData?.TranshipmentVessel || ''}
                                        placeholder="Select vessel"
                                        onchange={(val) => { if (!val) handleChange({ target: { name: 'TranshipmentVessel', value: '' } }); }}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Transshipment Voyage {isRequired("TranshipmentVoyage") && <span className="text-red-500">*</span>}</label>
                                    <input className={inputClass} placeholder="Transshipment Voyage" name="TranshipmentVoyage" value={formData?.TranshipmentVoyage ?? ''} onChange={handleChange} />
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className={labelClass}>Port of Discharge {isRequired("PortOfDischarge") && <span className="text-red-500">*</span>}</label>
                                    <SearchBarPagination
                                        searchAPI={'v1/AutoSuggestion/GetDestinationSuggestion?Prefix='}
                                        selectReacord={(d) => {
                                            handleChange({ target: { name: 'PortOfDischarge', value: d?.[1] } });
                                            handleChange({ target: { name: 'PortOfDischargeCode', value: _getPortCode(d?.[1]) } });
                                        }}
                                        value={PortOfDischarge}
                                        onchange={(val) => { if (!val) clearLocationPair('PortOfDischarge', 'PortOfDischargeCode'); }}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Show On HBL {isRequired("PortOfDischarge_Customized") && <span className="text-red-500">*</span>}</label>
                                    <input className={inputClass} placeholder="Show On HBL" name="PortOfDischarge_Customized" value={formData?.PortOfDischarge_Customized ?? ''} onChange={handleChange} />
                                </div>
                                <div>
                                    <label className={labelClass}>Terminal / Pier {isRequired("LoadingPierTerminal") && <span className="text-red-500">*</span>}</label>
                                    <input className={inputClass} placeholder="Terminal / Pier" name="LoadingPierTerminal" value={formData?.LoadingPierTerminal ?? ''} onChange={handleChange} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelClass}>Clearance Port {isRequired("ClearancePort") && <span className="text-red-500">*</span>}</label>
                                    <input className={inputClass} name="ClearancePort" value={formData?.ClearancePort ?? ''} onChange={handleChange} />
                                </div>
                                <div>
                                    <label className={labelClass}>Clearance Port Code {isRequired("ClearancePortCode") && <span className="text-red-500">*</span>}</label>
                                    <input className={inputClass} name="ClearancePortCode" value={formData?.ClearancePortCode ?? ''} onChange={handleChange} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelClass}>Carrier Place of Delivery {isRequired("CarrierPlaceOfDelivery") && <span className="text-red-500">*</span>}</label>
                                    <SearchBarPagination
                                        searchAPI={'v1/AutoSuggestion/GetDestinationSuggestion?Prefix='}
                                        selectReacord={(d) => {
                                            handleChange({ target: { name: 'CarrierPlaceOfDelivery', value: d?.[1] } });
                                            handleChange({ target: { name: 'CarrierPlaceOfDeliveryCode', value: _getPortCode(d?.[1]) } });
                                            handleChange({ target: { name: 'CarrierPlaceOfDelivery_Customized', value: d?.[1] ?? '' } });
                                        }}
                                        value={formData?.CarrierPlaceOfDelivery ?? ''}
                                        onchange={(val) => { if (!val) clearLocationPair('CarrierPlaceOfDelivery', 'CarrierPlaceOfDeliveryCode'); }}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Show On HBL {isRequired("CarrierPlaceOfDelivery_Customized") && <span className="text-red-500">*</span>}</label>
                                    <input className={inputClass} placeholder="Show On HBL" name="CarrierPlaceOfDelivery_Customized" value={formData?.CarrierPlaceOfDelivery_Customized ?? ''} onChange={handleChange} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelClass}>Organization Place of Delivery {isRequired("ShipezPlaceOfDelivery") && <span className="text-red-500">*</span>}</label>
                                    <SearchBarPagination
                                        searchAPI={'v1/AutoSuggestion/GetDestinationSuggestion?Prefix='}
                                        selectReacord={(d) => {
                                            handleChange({ target: { name: 'ShipezPlaceOfDelivery', value: d?.[1] } });
                                            handleChange({ target: { name: 'ShipezPlaceOfDeliveryCode', value: _getPortCode(d?.[1]) } });
                                            handleChange({ target: { name: 'ShipezPlaceOfDelivery_Customized', value: d?.[1] ?? '' } });
                                        }}
                                        value={formData?.ShipezPlaceOfDelivery ?? ''}
                                        onchange={(val) => { if (!val) clearLocationPair('ShipezPlaceOfDelivery', 'ShipezPlaceOfDeliveryCode'); }}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Show On HBL {isRequired("ShipezPlaceOfDeliveryCustomized") && <span className="text-red-500">*</span>}</label>
                                    <input className={inputClass} placeholder="Show On HBL" name="ShipezPlaceOfDeliveryCustomized" value={formData?.ShipezPlaceOfDeliveryCustomized ?? ''} onChange={handleChange} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Freight Location Card */}
                    <div className="bg-gradient-to-br from-slate-50 to-amber-50 dark:from-slate-900/50 dark:to-amber-950/20 rounded-xl border-l-2 border-l-amber-500 border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="p-1.5 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                                <Warehouse className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                            </div>
                            <h3 className="font-semibold text-amber-700 dark:text-amber-400 text-sm">Freight Location</h3>
                        </div>
                        <div>
                            <label className={labelClass}>Freight Loc/Unloading Terminal {isRequired("freightLocUnloadingTerminal") && <span className="text-red-500">*</span>}</label>
                            <SearchBarPagination
                                searchAPI={'v1/AutoSuggestion/GetConsigneeSuggestion?Prefix='}
                                selectReacord={(d) => {
                                    handleChange({ target: { name: 'FreightLocationName', value: d?.[1] } });
                                    handleChange({ target: { name: 'FreightLocationID', value: d?.[0] } });
                                    setAddress('FreightLocationAddress', d?.[0]);
                                }}
                                onchange={(val) => {
                                    if (!val) {
                                        handleChange({ target: { name: 'FreightLocationName', value: '' } });
                                        handleChange({ target: { name: 'FreightLocationID', value: 0 } });
                                        setAddress('FreightLocationAddress', '');
                                    }
                                }}
                                value={formData?.FreightLocationName || ''}
                            />
                            <label className={labelClass + " mt-2"}>Address {isRequired("FreightLocationAddress") && <span className="text-red-500">*</span>}</label>
                            <textarea className={inputClass} rows={2} placeholder="Enter Address" name="FreightLocationAddress" value={formData?.FreightLocationAddress} onChange={handleChange} />
                        </div>
                    </div>

                    {/* Codes & Extra Dates Card */}
                    <div className="bg-gradient-to-br from-slate-50 to-purple-50 dark:from-slate-900/50 dark:to-purple-950/20 rounded-xl border-l-2 border-l-purple-500 border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="p-1.5 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                                <Hash className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                            </div>
                            <h3 className="font-semibold text-purple-700 dark:text-purple-400 text-sm">Codes & Additional Dates</h3>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelClass}>Firms Code {isRequired("FirmsCode") && <span className="text-red-500">*</span>}</label>
                                <input className={inputClass} placeholder="Firms Code" name="FirmsCode" value={formData?.FirmsCode} onChange={handleChange} />
                            </div>
                            <div>
                                <label className={labelClass}>Entry No. {isRequired("EntryNo") && <span className="text-red-500">*</span>}</label>
                                <input className={inputClass} placeholder="Entry No." name="EntryNo" value={formData?.EntryNo} onChange={handleChange} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mt-2">
                            {deliveryFields1.map(({ label, key, type }) => (
                                <div key={key}>
                                    <label className={labelClass}>{label} {isRequired(key) && <span className="text-red-500">*</span>}</label>
                                    {type === "date" ? (
                                        <DateInputwithcal onError={handleDateError} label={label} name={key} value={formData[key] ? new Date(formData[key]) : null} onChange={handleDateChange} required />
                                    ) : (
                                        <input className={inputClass} type="text" name={key} value={formData[key] || ""} onChange={handleChange} />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ================= RIGHT PANEL ================= */}
                <div className="space-y-5">
                    {/* Country & Move Type Card */}
                    <div className="bg-gradient-to-br from-slate-50 to-emerald-50 dark:from-slate-900/50 dark:to-emerald-950/20 rounded-xl border-l-2 border-l-emerald-500 border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="p-1.5 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                                <Globe className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                            </div>
                            <h3 className="font-semibold text-emerald-700 dark:text-emerald-400 text-sm">General Settings</h3>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelClass}>Country of Origin {isRequired("CountryOfOrigin") && <span className="text-red-500">*</span>}</label>
                                <select className={selectClass} value={formData?.CountryOfOrigin ?? ''} onChange={handleChange} name="CountryOfOrigin">
                                    <option value="">select Country</option>
                                    {countryList?.map((country, key) => (
                                        <option key={key} value={country.incoterm ?? ''}>{country.incoterm}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className={labelClass}>Move Type {isRequired("MoveType") && <span className="text-red-500">*</span>}</label>
                                <select name="MoveType" value={formData?.MoveType ?? ''} onChange={handleChange} className={selectClass}>
                                    <option value="">SELECT TYPE</option>
                                    {moveTypeList.map((item, index) => (
                                        <option key={index} value={item.moveType}>{item.moveType}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="flex items-center gap-4 mt-3">
                            <label className="text-xs flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                                <input type="checkbox" name="MoveTypeHideOnHBL" checked={!!formData?.MoveTypeHideOnHBL} onChange={handleChange} className="rounded" /> Hide On HBL
                            </label>
                            <label className="text-xs flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                                <input type="checkbox" name="IsForAMS" checked={!!formData?.IsForAMS} onChange={handleChange} className="rounded" /> For AMS
                            </label>
                        </div>
                    </div>

                    {/* Key Dates Card */}
                    <div className="bg-gradient-to-br from-slate-50 to-rose-50 dark:from-slate-900/50 dark:to-rose-950/20 rounded-xl border-l-2 border-l-rose-500 border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="p-1.5 bg-rose-100 dark:bg-rose-900/30 rounded-lg">
                                <Calendar className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                            </div>
                            <h3 className="font-semibold text-rose-700 dark:text-rose-400 text-sm">Key Dates</h3>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            {dateFields.map(({ label, key }) => (
                                <div key={key}>
                                    <label className={labelClass}>{label} {isRequired(key) && <span className="text-red-500">*</span>}</label>
                                    <DateInputwithcal onError={handleDateError} label={label} name={key} value={formData[key] ? new Date(formData[key]) : null} onChange={handleDateChange} required />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Commercial & Containers Card */}
                    <div className="bg-gradient-to-br from-slate-50 to-orange-50 dark:from-slate-900/50 dark:to-orange-950/20 rounded-xl border-l-2 border-l-orange-500 border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="p-1.5 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                                <Package className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                            </div>
                            <h3 className="font-semibold text-orange-700 dark:text-orange-400 text-sm">Commercial & Containers</h3>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelClass}>Commercial Documents Received {isRequired("CommercialDocumentsReceivedDate") && <span className="text-red-500">*</span>}</label>
                                <DateInputwithcal onError={handleDateError} label="Commercial Documents Received" name="CommercialDocumentsReceivedDate" value={formData?.CommercialDocumentsReceivedDate ? new Date(formData?.CommercialDocumentsReceivedDate) : null} onChange={handleDateChange} required />
                            </div>
                            <div>
                                <label className={labelClass}>Containers Available Date {isRequired("ContainersAvailableDate") && <span className="text-red-500">*</span>}</label>
                                <DateInputwithcal onError={handleDateError} label="Containers Available Date" name="ContainersAvailableDate" value={formData?.ContainersAvailableDate ? new Date(formData?.ContainersAvailableDate) : null} onChange={handleDateChange} required />
                            </div>
                        </div>
                    </div>

                    {/* Delivery Status Card - IMPROVED */}
                    <div className="bg-gradient-to-br from-slate-50 to-yellow-50 dark:from-slate-900/50 dark:to-yellow-950/20 rounded-xl border-l-2 border-l-yellow-500 border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="p-1.5 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                                <Truck className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                            </div>
                            <h3 className="font-semibold text-yellow-700 dark:text-yellow-400 text-sm">Delivery Status</h3>
                        </div>
                        <div className="flex flex-wrap gap-4">
                            {[
                                { value: "liveUnload", label: "Live Unload", icon: <Clock className="w-4 h-4" />, color: "blue" },
                                { value: "dropPick", label: "Drop & Pick", icon: <TruckIcon className="w-4 h-4" />, color: "green" },
                                { value: "", label: "No Status", icon: <XCircle className="w-4 h-4" />, color: "red" },
                            ].map((opt) => (
                                <label key={opt.value || 'noStatus'} className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-all ${deliveryStatusNormalized === opt.value
                                    ? `bg-${opt.color}-100 dark:bg-${opt.color}-900/30 border-2 border-${opt.color}-500 shadow-sm`
                                    : "bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700"
                                    }`}>
                                    <input
                                        type="radio"
                                        name="DeliveryStatus"
                                        value={opt.value}
                                        checked={deliveryStatusNormalized === opt.value}
                                        onChange={handleChange}
                                        className="w-3.5 h-3.5 text-yellow-600 focus:ring-yellow-500"
                                    />
                                    <span className={`text-sm font-medium ${deliveryStatusNormalized === opt.value ? `text-${opt.color}-700 dark:text-${opt.color}-300` : "text-slate-700 dark:text-slate-300"}`}>
                                        {opt.label}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Delivery Details Card */}
                    <div className="bg-gradient-to-br from-slate-50 to-sky-50 dark:from-slate-900/50 dark:to-sky-950/20 rounded-xl border-l-2 border-l-sky-500 border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="p-1.5 bg-sky-100 dark:bg-sky-900/30 rounded-lg">
                                <ClipboardList className="w-4 h-4 text-sky-600 dark:text-sky-400" />
                            </div>
                            <h3 className="font-semibold text-sky-700 dark:text-sky-400 text-sm">Delivery Details</h3>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            {deliveryFields.map(({ label, key, type }) => (
                                <div key={key}>
                                    <label className={labelClass}>{label} {isRequired(key) && <span className="text-red-500">*</span>}</label>
                                    {type === "date" ? (
                                        <DateInputwithcal onError={handleDateError} label={label} name={key} value={formData[key] ? new Date(formData[key]) : null} onChange={handleDateChange} required />
                                    ) : (
                                        <input className={inputClass} type="text" name={key} value={formData[key] || ""} onChange={handleChange} />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default memo(RoutingInformation);